import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/store';
import { fetchTransactions, exportTransactions } from '@/store/slices/walletSlice';
import { TransactionList } from '@/components/wallet';
import { FiFilter, FiDownload, FiSearch, FiChevronLeft, FiChevronRight, FiArrowRight, FiArrowLeft } from 'react-icons/fi';

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { transactions, loading } = useSelector((state: RootState) => state.wallet);

  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<any>({
    type: '',
    status: '',
    currency: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  useEffect(() => {
    loadTransactions(currentPage, filters);
  }, [currentPage]);

  const buildActiveFilters = (sourceFilters: any) => {
    const activeFilters: any = {};
    if (sourceFilters.type) activeFilters.type = sourceFilters.type;
    if (sourceFilters.status) activeFilters.status = sourceFilters.status;
    if (sourceFilters.currency) activeFilters.currency = sourceFilters.currency;
    if (sourceFilters.startDate) activeFilters.startDate = sourceFilters.startDate;
    if (sourceFilters.endDate) activeFilters.endDate = sourceFilters.endDate;
    if (sourceFilters.search) activeFilters.search = sourceFilters.search;
    return activeFilters;
  };

  const loadTransactions = (page = currentPage, sourceFilters = filters) => {
    dispatch(fetchTransactions({ filters: buildActiveFilters(sourceFilters), page, limit: 20 }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadTransactions(1, filters);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      type: '',
      status: '',
      currency: '',
      startDate: '',
      endDate: '',
      search: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadTransactions(1, clearedFilters);
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    dispatch(exportTransactions({ filters: buildActiveFilters(filters), format }));
    setShowExportMenu(false);
  };

  const transactionTypes = [
    { value: 'deposit', label: 'Deposit' },
    { value: 'withdrawal', label: 'Withdrawal' },
    { value: 'transfer_in', label: 'Transfer In' },
    { value: 'transfer_out', label: 'Transfer Out' },
    { value: 'escrow_release', label: 'Escrow Release' },
    { value: 'escrow_hold', label: 'Escrow Hold' },
    { value: 'commission_deduction', label: 'Commission' },
    { value: 'refund', label: 'Refund' },
  ];

  const transactionStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const currencies = [
    { value: 'GHS', label: 'GHS' },
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
  ];

  const totalPages = transactions?.pagination?.totalPages || 1;
  const hasFilters = Object.values(filters).some((val) => val !== '');

  return (
    <>
      <Helmet>
        <title>Transaction History - SideGO Wallet</title>
      </Helmet>

      <div className="relative py-1 sm:py-2">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-cyan-100/60 to-transparent dark:from-cyan-900/20" />

        <div className="mb-3 sm:mb-4">
          <button
            onClick={() => navigate('/dashboard/wallet')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Wallet
          </button>
        </div>

        <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/85 sm:mb-8 sm:p-6">
          <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              Wallet Ledger
            </span>
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
              Advanced Filters
            </span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">
            Transaction History
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-gray-400 sm:text-base">
            View and manage all your wallet transactions
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="border-b border-slate-200 p-3 dark:border-gray-800 sm:p-4">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
              <div className="w-full sm:max-w-md">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                    className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
                    showFilters || hasFilters
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <FiFilter size={16} />
                  Filters {hasFilters && `(${Object.values(filters).filter((v) => v).length})`}
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu((prev) => !prev)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:px-4"
                  >
                    <FiDownload size={16} />
                    Export
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 z-10 mt-2 w-40 rounded-xl border border-slate-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full rounded-t-xl px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full rounded-b-xl px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Export as PDF
                    </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="mt-4 border-t border-slate-200 pt-4 dark:border-gray-800">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
                  <div>
                    <label htmlFor="transactions-filter-type" className="mb-1 block text-sm font-medium text-slate-700 dark:text-gray-300">
                      Type
                    </label>
                    <select
                      id="transactions-filter-type"
                      value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">All Types</option>
                      {transactionTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="transactions-filter-status" className="mb-1 block text-sm font-medium text-slate-700 dark:text-gray-300">
                      Status
                    </label>
                    <select
                      id="transactions-filter-status"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">All Statuses</option>
                      {transactionStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="transactions-filter-currency" className="mb-1 block text-sm font-medium text-slate-700 dark:text-gray-300">
                      Currency
                    </label>
                    <select
                      id="transactions-filter-currency"
                      value={filters.currency}
                      onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">All Currencies</option>
                      {currencies.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-gray-300">
                      Date Range
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="transactions-filter-start-date"
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                        aria-label="Start date"
                        className="flex-1 rounded-xl border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                      <input
                        id="transactions-filter-end-date"
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                        aria-label="End date"
                        className="flex-1 rounded-xl border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={handleApplyFilters}
                    className="inline-flex items-center gap-1 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
                  >
                    Apply Filters
                    <FiArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleClearFilters}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 sm:p-5">
            <TransactionList
              transactions={transactions?.transactions || []}
              loading={loading}
              emptyMessage="No transactions found"
            />
          </div>

          {transactions?.pagination && transactions.pagination.totalPages > 1 && (
            <div className="border-t border-slate-200 p-3 dark:border-gray-800 sm:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                  Showing {((currentPage - 1) * 20) + 1} to{' '}
                  {Math.min(currentPage * 20, transactions.pagination.totalItems)} of{' '}
                  {transactions.pagination.totalItems} transactions
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Go to previous page"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    <FiChevronLeft />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`rounded-xl px-3 py-2 text-sm ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Go to next page"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TransactionsPage;
